import { requireService, wrap } from '@repo/di-ez';
import { HistoryLightCustom, ZoomLightCustom, ZoomOutCustom } from '@repo/go-icons';
import { Button, Loader } from '@repo/go-ui';
import { hasValue } from '@repo/utils';
import * as echarts from 'echarts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrajectoryChartWarnings } from '../trajectory-chart-warnings';
import {
  computeCenteredCartesianRange,
  computeFittedCellRange,
  computeLeftShiftedCartesianRange,
  computePlanSquareRanges,
  niceFloorInterval,
  padInvertedZeroTop,
  TARGET_GRID_LINES_2D,
  type TAxisRange,
} from '../utils/chart-axis-layout';
import { formatGridLabel, formatTooltipNumber } from '../utils/format-chart-number';

import type {
  Trajectory2dChartPoint,
  Trajectory2dMappedChart,
  Trajectory2dSeriesData,
} from './map-trajectory-points-by-config';
import { mapTrajectoryPointsByConfig } from './map-trajectory-points-by-config';
import type { Trajectory2dChartConfig, Trajectory2dChartTypeId } from './trajectory-2d-chart-configs';
import styles from './trajectory-2d-chart.module.scss';

const MIN_GRID_LINES = TARGET_GRID_LINES_2D;

type AxisRange = TAxisRange;
type ChartAxisLayout = 'square' | 'rectangle' | 'independent' | 'shiftedRectangle' | 'shiftedIndependent' | 'fitted';

type ZoomWindow = {
  start: number;
  end: number;
};

type ChartZoomState = {
  x: ZoomWindow;
  y: ZoomWindow;
};

type ChartGridIntervals = {
  x: number[];
  y: number;
};

const DEFAULT_ZOOM_WINDOW: ZoomWindow = { start: 0, end: 100 };
const ZOOM_SCALE_FACTOR = 1.25;
const MIN_ZOOM_WINDOW = 2;
const chartZoomById = new Map<string, ChartZoomState>();

function getInitialZoom(chartId: string): ChartZoomState {
  return chartZoomById.get(chartId) ?? { x: { ...DEFAULT_ZOOM_WINDOW }, y: { ...DEFAULT_ZOOM_WINDOW } };
}

function getZoomWindowSize(window: ZoomWindow): number {
  return Math.max(0, window.end - window.start);
}

function isMaxZoom(zoom: ChartZoomState): boolean {
  return getZoomWindowSize(zoom.x) <= MIN_ZOOM_WINDOW && getZoomWindowSize(zoom.y) <= MIN_ZOOM_WINDOW;
}

function isMinZoom(zoom: ChartZoomState): boolean {
  return zoom.x.start <= 0 && zoom.x.end >= 100 && zoom.y.start <= 0 && zoom.y.end >= 100;
}

function getVisibleExtent(full: AxisRange, window: ZoomWindow): { min: number; max: number } {
  const span = full.max - full.min;

  if (!(span > 0)) {
    return { min: full.min, max: full.max };
  }

  return {
    min: full.min + (span * window.start) / 100,
    max: full.min + (span * window.end) / 100,
  };
}

function intervalForVisibleWindow(full: AxisRange, window: ZoomWindow): number {
  const span = full.max - full.min;
  const visibleSpan = Math.max((span * (window.end - window.start)) / 100, Number.EPSILON);

  return niceFloorInterval(visibleSpan / MIN_GRID_LINES);
}

function shiftZoomWindow(window: ZoomWindow, deltaPercent: number): ZoomWindow {
  const size = window.end - window.start;
  let start = window.start + deltaPercent;
  let end = window.end + deltaPercent;

  if (start < 0) {
    start = 0;
    end = size;
  }

  if (end > 100) {
    end = 100;
    start = 100 - size;
  }

  return { start, end };
}

function clampZoomWindow(start: number, end: number): ZoomWindow {
  let nextStart = start;
  let nextEnd = end;

  if (nextStart < 0) {
    nextEnd -= nextStart;
    nextStart = 0;
  }

  if (nextEnd > 100) {
    nextStart -= nextEnd - 100;
    nextEnd = 100;
  }

  return {
    start: Math.max(0, nextStart),
    end: Math.min(100, nextEnd),
  };
}

function scaleZoomWindow(window: ZoomWindow, nextSize: number, focusPercent?: number | null): ZoomWindow {
  const currentSize = Math.max(MIN_ZOOM_WINDOW, window.end - window.start);
  const size = Math.min(100, Math.max(MIN_ZOOM_WINDOW, nextSize));
  const anchor =
    hasValue(focusPercent) && Number.isFinite(focusPercent)
      ? Math.min(100, Math.max(0, focusPercent))
      : (window.start + window.end) / 2;
  const ratio = currentSize > 0 ? (anchor - window.start) / currentSize : 0.5;
  const start = anchor - ratio * size;

  return clampZoomWindow(start, start + size);
}

function zoomAxisWindow(window: ZoomWindow, direction: 'in' | 'out', focusPercent?: number | null): ZoomWindow {
  const currentSize = Math.max(MIN_ZOOM_WINDOW, window.end - window.start);
  const nextSize = direction === 'in' ? currentSize / ZOOM_SCALE_FACTOR : currentSize * ZOOM_SCALE_FACTOR;

  return scaleZoomWindow(window, nextSize, focusPercent);
}

function valueToZoomPercent(value: number, range: AxisRange): number {
  const span = range.max - range.min;

  if (!(span > 0)) {
    return 50;
  }

  return ((value - range.min) / span) * 100;
}

function getAxisFields(mapped: Trajectory2dMappedChart) {
  return [...new Map(mapped.series.map((series) => [series.field, series])).values()];
}

function getChartAxisLayout(chartTypeId: Trajectory2dChartTypeId): ChartAxisLayout {
  switch (chartTypeId) {
    case 'planNsWe':
      return 'square';
    case 'closureMd':
    case 'tvdMd':
    case 'tvdClosure':
    case 'tvdAhd':
      return 'shiftedRectangle';
    case 'incAziMd':
    case 'dlsMd':
      return 'shiftedIndependent';
    case 'ctcMd':
      return 'fitted';
    default:
      return 'rectangle';
  }
}

function collectSeriesValues(
  mapped: Trajectory2dMappedChart,
  xField?: Trajectory2dSeriesData['field'],
): { x: number[]; y: number[] } {
  const seriesList = xField ? mapped.series.filter((series) => series.field === xField) : mapped.series;

  return {
    x: seriesList.flatMap((series) => series.data.map(([x]) => x)),
    y: mapped.series.flatMap((series) => series.data.map(([, y]) => y)),
  };
}

function resolveFullAxisRanges(
  mapped: Trajectory2dMappedChart,
  layout: ChartAxisLayout,
): { xByField: Map<string, AxisRange>; y: AxisRange; defaultX: AxisRange } {
  const { x: allX, y: allY } = collectSeriesValues(mapped);
  const axisFields = getAxisFields(mapped);

  const withSharedX = (xRange: AxisRange, yRange: AxisRange) => ({
    xByField: new Map(axisFields.map((series) => [String(series.field), xRange])),
    y: yRange,
    defaultX: xRange,
  });

  const fitVertical = (values: number[]) => {
    const range = computeFittedCellRange(values);

    return mapped.yInvert ? padInvertedZeroTop(range) : range;
  };

  switch (layout) {
    case 'square': {
      const { x, y } = computePlanSquareRanges(allX, allY);
      return withSharedX(x, y);
    }
    case 'rectangle': {
      return withSharedX(computeCenteredCartesianRange(allX), fitVertical(allY));
    }
    case 'fitted': {
      return withSharedX(computeFittedCellRange(allX), fitVertical(allY));
    }
    case 'shiftedRectangle': {
      return withSharedX(computeLeftShiftedCartesianRange(allX), fitVertical(allY));
    }
    case 'independent':
    case 'shiftedIndependent': {
      const resolveX =
        layout === 'shiftedIndependent' ? computeLeftShiftedCartesianRange : computeCenteredCartesianRange;
      const y = fitVertical(allY);
      const xByField = new Map(
        axisFields.map((series) => {
          const { x } = collectSeriesValues(mapped, series.field);

          return [String(series.field), resolveX(x)] as const;
        }),
      );

      return {
        xByField,
        y,
        defaultX: resolveX(allX),
      };
    }
  }
}

function computeGridIntervals(
  mapped: Trajectory2dMappedChart,
  zoom: ChartZoomState,
  layout: ChartAxisLayout,
): ChartGridIntervals {
  const axisFields = getAxisFields(mapped);
  const ranges = resolveFullAxisRanges(mapped, layout);
  const xIntervals = axisFields.map((series) => {
    const range = ranges.xByField.get(String(series.field)) ?? ranges.defaultX;

    return intervalForVisibleWindow(range, zoom.x);
  });

  if (!xIntervals.length) {
    xIntervals.push(intervalForVisibleWindow(ranges.defaultX, zoom.x));
  }

  return {
    x: xIntervals,
    y: intervalForVisibleWindow(ranges.y, zoom.y),
  };
}
type HoveredSeriesPoint = {
  series: Trajectory2dSeriesData;
  seriesIndex: number;
  dataIndex: number;
  point: Trajectory2dChartPoint;
};

function areCoordinatesEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= Math.max(1e-6, Math.abs(left) * 1e-9, Math.abs(right) * 1e-9);
}

function findNearestSeriesPoint(
  seriesList: Trajectory2dSeriesData[],
  pointerX: number,
  pointerY: number,
  xSpan: number,
  ySpan: number,
): HoveredSeriesPoint | null {
  const normalizedXSpan = Math.max(Math.abs(xSpan), Number.EPSILON);
  const normalizedYSpan = Math.max(Math.abs(ySpan), Number.EPSILON);

  let best: HoveredSeriesPoint | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  seriesList.forEach((series, seriesIndex) => {
    series.data.forEach((point, dataIndex) => {
      const dx = (point[0] - pointerX) / normalizedXSpan;
      const dy = (point[1] - pointerY) / normalizedYSpan;
      const distance = dx * dx + dy * dy;

      if (distance < bestDistance) {
        bestDistance = distance;
        best = { series, seriesIndex, dataIndex, point };
      }
    });
  });

  return best;
}

function collectTooltipSeriesPoints(
  seriesList: Trajectory2dSeriesData[],
  hovered: HoveredSeriesPoint,
): HoveredSeriesPoint[] {
  const result: HoveredSeriesPoint[] = [hovered];

  seriesList.forEach((series, seriesIndex) => {
    if (seriesIndex === hovered.seriesIndex) {
      return;
    }

    series.data.forEach((point, dataIndex) => {
      const sameAxis =
        (series.axisIndex === hovered.series.axisIndex && areCoordinatesEqual(point[0], hovered.point[0])) ||
        areCoordinatesEqual(point[1], hovered.point[1]);

      if (!sameAxis) {
        return;
      }

      result.push({ series, seriesIndex, dataIndex, point });
    });
  });

  return result;
}

function seriesDisplayName(series: Trajectory2dSeriesData, xLabel: string): string {
  return `${series.legendLabel} · ${xLabel}`;
}

function formatTrajectory2dTooltipHtml(
  tooltipPoints: HoveredSeriesPoint[],
  xLabelsByField: Map<string, string>,
  yLabel: string,
): string {
  if (!tooltipPoints.length) {
    return '';
  }

  return tooltipPoints
    .map((item) => {
      const xLabel = xLabelsByField.get(String(item.series.field)) ?? item.series.field;

      return [
        item.series.legendLabel,
        `${xLabel}: ${formatTooltipNumber(item.point[0])}`,
        `${yLabel}: ${formatTooltipNumber(item.point[1])}`,
      ].join('<br/>');
    })
    .join('<br/><br/>');
}

function buildGridAxisLabel(ticks: number[]) {
  return {
    customValues: ticks,
    formatter: (value: number) => formatGridLabel(value),
  };
}

function buildTickValues(min: number, max: number, interval: number): number[] {
  if (!(interval > 0) || !Number.isFinite(interval) || !Number.isFinite(min) || !Number.isFinite(max)) {
    return [];
  }

  const first = Math.ceil(min / interval - 1e-9);
  const last = Math.floor(max / interval + 1e-9);
  const ticks: number[] = [];

  for (let index = first; index <= last; index += 1) {
    ticks.push(Number((index * interval).toPrecision(12)));
  }

  return ticks;
}

function buildAxisView(
  fullRange: AxisRange,
  window: ZoomWindow,
  interval: number,
): { min: number; max: number; interval: number; ticks: number[] } {
  const visible = getVisibleExtent(fullRange, window);

  return {
    min: visible.min,
    max: visible.max,
    interval,
    ticks: buildTickValues(visible.min, visible.max, interval),
  };
}

function buildChartOption(
  mapped: Trajectory2dMappedChart,
  xLabelsByField: Map<string, string>,
  yLabel: string,
  getPointer: () => { x: number | null; y: number | null },
  zoom: ChartZoomState,
  intervals: ChartGridIntervals,
  getIsPanning: () => boolean,
  layout: ChartAxisLayout,
): echarts.EChartsOption {
  const axisFields = getAxisFields(mapped);
  const isDualX = axisFields.length > 1;
  const ranges = resolveFullAxisRanges(mapped, layout);
  const yView = buildAxisView(ranges.y, zoom.y, intervals.y);
  const xSpan = ranges.defaultX.max - ranges.defaultX.min;
  const ySpan = ranges.y.max - ranges.y.min;

  const xAxes: echarts.XAXisComponentOption[] = axisFields.map((series, index) => {
    const range = ranges.xByField.get(String(series.field)) ?? ranges.defaultX;
    const interval = intervals.x[index] ?? range.interval;
    const xView = buildAxisView(range, zoom.x, interval);

    return {
      type: 'value',
      name: xLabelsByField.get(String(series.field)) ?? '',
      nameLocation: 'middle',
      nameGap: 28 + (isDualX && index === 1 ? 24 : 0),
      min: xView.min,
      max: xView.max,
      interval: xView.interval,
      minInterval: xView.interval,
      maxInterval: xView.interval,
      axisTick: { customValues: xView.ticks },
      axisLabel: buildGridAxisLabel(xView.ticks),
      position: index === 0 ? 'bottom' : 'top',
      offset: isDualX && index === 1 ? 0 : undefined,
      splitLine: index === 0 ? { lineStyle: { opacity: 0.35 } } : { show: false },
      axisLine: { show: true },
    };
  });

  if (!xAxes.length) {
    const interval = intervals.x[0] ?? ranges.defaultX.interval;
    const xView = buildAxisView(ranges.defaultX, zoom.x, interval);

    xAxes.push({
      type: 'value',
      name: '',
      nameLocation: 'middle',
      nameGap: 28,
      min: xView.min,
      max: xView.max,
      interval: xView.interval,
      minInterval: xView.interval,
      maxInterval: xView.interval,
      axisTick: { customValues: xView.ticks },
      axisLabel: buildGridAxisLabel(xView.ticks),
      splitLine: { lineStyle: { opacity: 0.35 } },
    });
  }

  return {
    animation: false,
    grid: {
      left: 56,
      right: 20,
      top: isDualX ? 52 : 28,
      bottom: 48,
      containLabel: false,
    },
    tooltip: {
      trigger: 'axis',
      triggerOn: 'none',
      show: !getIsPanning(),
      className: `${styles.tooltip} caption`,
      backgroundColor: 'rgba(20, 21, 26, 0.92)',
      borderWidth: 0,
      padding: [8, 10],
      textStyle: {
        color: 'var(--contrast)',
      },
      axisPointer: {
        type: 'cross',
        snap: false,
        show: !getIsPanning(),
      },
      formatter: () => {
        if (getIsPanning()) {
          return '';
        }

        const pointer = getPointer();

        if (!hasValue(pointer.x) || !hasValue(pointer.y)) {
          return '';
        }

        const hovered = findNearestSeriesPoint(mapped.series, pointer.x, pointer.y, xSpan, ySpan);

        if (!hovered) {
          return '';
        }

        const tooltipPoints = collectTooltipSeriesPoints(mapped.series, hovered);

        return formatTrajectory2dTooltipHtml(tooltipPoints, xLabelsByField, yLabel);
      },
    },
    xAxis: xAxes,
    yAxis: {
      type: 'value',
      name: yLabel,
      inverse: mapped.yInvert,
      nameLocation: 'center',
      nameGap: 48,
      min: yView.min,
      max: yView.max,
      interval: yView.interval,
      minInterval: yView.interval,
      maxInterval: yView.interval,
      axisTick: { customValues: yView.ticks },
      axisLabel: buildGridAxisLabel(yView.ticks),
      splitLine: { lineStyle: { opacity: 0.35 } },
    },
    series: mapped.series.map((series) => ({
      type: 'line',
      name: seriesDisplayName(series, xLabelsByField.get(String(series.field)) ?? String(series.field)),
      data: series.data,
      showSymbol: false,
      smooth: false,
      lineStyle: { width: series.lineWidth, color: series.color },
      itemStyle: { color: series.color },
      xAxisIndex: series.axisIndex,
    })),
  };
}

type Props = {
  config: Trajectory2dChartConfig;
};

export const Trajectory2dChart = wrap(function Trajectory2dChart({ config }: Props) {
  const { t } = useTranslation();
  const graphStore = requireService('trajectoryGraphStore');
  const ctcStore = requireService('trajectoryCtcStore');
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const pointerXRef = useRef<number | null>(null);
  const pointerYRef = useRef<number | null>(null);
  const activeHoverKeyRef = useRef<string | null>(null);
  const isPanningRef = useRef(false);
  const isHoveringChartRef = useRef(false);
  const zoomFocusRef = useRef<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState<ChartZoomState>(() => getInitialZoom(config.id));
  const zoomRef = useRef<ChartZoomState>(zoom);
  zoomRef.current = zoom;
  const intervalsRef = useRef<ChartGridIntervals | null>(null);
  const panRef = useRef<{
    startX: number;
    startY: number;
    startZoom: ChartZoomState;
    pixelToXPercent: number;
    pixelToYPercent: number;
  } | null>(null);

  const trajectories = graphStore.graphTrajectories;
  const plotRevision = graphStore.graphPlotRevision;
  const activeAziVertSectDeg = graphStore.activeAziVertSectDeg;
  const { id: chartTypeId, xFields, yField } = config;
  const isDualX = xFields.length > 1;
  const axisLayout = getChartAxisLayout(chartTypeId);
  const activeLegendLabel = t('Graph.legend.active');

  const stationMapped = useMemo(() => {
    if (chartTypeId === 'ctcMd') {
      return null;
    }

    return mapTrajectoryPointsByConfig(
      trajectories,
      { id: chartTypeId, xFields, yField },
      { activeAziVertSectDeg, activeLegendLabel },
    );
    // TODO: need to resolve hidden dependencies
    // plotRevision: deep station / aziRef changes; trajectories identity alone is not enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- plotRevision is intentional
  }, [plotRevision, trajectories, chartTypeId, xFields, yField, activeAziVertSectDeg, activeLegendLabel]);

  const mapped = stationMapped ?? ctcStore.mappedChart;

  const mappedRef = useRef(mapped);
  mappedRef.current = mapped;

  const hasData = mapped.series.some((series) => series.data.length > 0);
  const xLabelsByField = useMemo(
    () => new Map(xFields.map((field) => [String(field.field), t(field.labelKey)])),
    [xFields, t],
  );
  const yLabel = t(yField.labelKey);
  const legendItems = useMemo(() => {
    if (!isDualX) {
      const seen = new Set<number>();

      return mapped.series.flatMap((series) => {
        if (seen.has(series.trajectoryId)) {
          return [];
        }

        seen.add(series.trajectoryId);

        return [{ key: String(series.trajectoryId), color: series.color, label: series.legendLabel }];
      });
    }

    const yShort = t(yField.labelKey);

    return mapped.series.map((series) => {
      const xShort = xLabelsByField.get(String(series.field)) ?? String(series.field);

      return {
        key: `${series.trajectoryId}-${String(series.field)}`,
        color: series.color,
        label: `${series.legendLabel} ${xShort}-${yShort}`,
      };
    });
  }, [isDualX, mapped.series, t, xLabelsByField, yField.labelKey]);
  const canZoomIn = !isMaxZoom(zoom);
  const canZoomOut = !isMinZoom(zoom);

  const applyViewport = (nextZoom: ChartZoomState, recomputeIntervals: boolean) => {
    const chart = chartRef.current;
    const currentMapped = mappedRef.current;

    if (recomputeIntervals || !intervalsRef.current) {
      intervalsRef.current = computeGridIntervals(currentMapped, nextZoom, axisLayout);
    }

    const intervals = intervalsRef.current;

    zoomRef.current = nextZoom;
    chartZoomById.set(config.id, nextZoom);

    if (!chart || !intervals) {
      setZoom(nextZoom);
      return;
    }

    if (recomputeIntervals) {
      activeHoverKeyRef.current = null;
      setZoom(nextZoom);
      chart.setOption(
        buildChartOption(
          currentMapped,
          xLabelsByField,
          yLabel,
          () => ({ x: pointerXRef.current, y: pointerYRef.current }),
          nextZoom,
          intervals,
          () => isPanningRef.current,
          axisLayout,
        ),
        true,
      );
      return;
    }

    const axisFields = getAxisFields(currentMapped);
    const ranges = resolveFullAxisRanges(currentMapped, axisLayout);
    const yView = buildAxisView(ranges.y, nextZoom.y, intervals.y);

    const xAxisOption = axisFields.map((series, index) => {
      const range = ranges.xByField.get(String(series.field)) ?? ranges.defaultX;
      const xView = buildAxisView(range, nextZoom.x, intervals.x[index] ?? range.interval);

      return {
        min: xView.min,
        max: xView.max,
        interval: xView.interval,
        minInterval: xView.interval,
        maxInterval: xView.interval,
        axisTick: { customValues: xView.ticks },
        axisLabel: buildGridAxisLabel(xView.ticks),
      };
    });

    if (!xAxisOption.length) {
      const xView = buildAxisView(ranges.defaultX, nextZoom.x, intervals.x[0] ?? ranges.defaultX.interval);

      xAxisOption.push({
        min: xView.min,
        max: xView.max,
        interval: xView.interval,
        minInterval: xView.interval,
        maxInterval: xView.interval,
        axisTick: { customValues: xView.ticks },
        axisLabel: buildGridAxisLabel(xView.ticks),
      });
    }

    chart.setOption(
      {
        xAxis: xAxisOption,
        yAxis: {
          min: yView.min,
          max: yView.max,
          interval: yView.interval,
          minInterval: yView.interval,
          maxInterval: yView.interval,
          axisTick: { customValues: yView.ticks },
          axisLabel: buildGridAxisLabel(yView.ticks),
        },
      },
      { lazyUpdate: true },
    );
  };
  const applyViewportRef = useRef(applyViewport);
  applyViewportRef.current = applyViewport;

  const resolveZoomFocusFromPixel = (offsetX: number, offsetY: number): { x: number; y: number } | null => {
    const chart = chartRef.current;
    const currentMapped = mappedRef.current;

    if (!chart || !currentMapped.series.length || !chart.containPixel('grid', [offsetX, offsetY])) {
      return null;
    }

    const pointerData = chart.convertFromPixel({ seriesIndex: 0 }, [offsetX, offsetY]);

    if (!pointerData) {
      return null;
    }

    const ranges = resolveFullAxisRanges(currentMapped, axisLayout);

    return {
      x: valueToZoomPercent(pointerData[0], ranges.defaultX),
      y: valueToZoomPercent(pointerData[1], ranges.y),
    };
  };

  const syncIdleCursor = () => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const cursor = isMinZoom(zoomRef.current) ? 'default' : 'grab';

    chart?.getZr().setCursorStyle(cursor);

    if (container) {
      container.style.cursor = cursor;
    }
  };

  const zoomIn = () => {
    if (isMaxZoom(zoomRef.current)) {
      return;
    }

    const focus = zoomFocusRef.current;

    applyViewport(
      {
        x: zoomAxisWindow(zoomRef.current.x, 'in', focus?.x),
        y: zoomAxisWindow(zoomRef.current.y, 'in', focus?.y),
      },
      true,
    );
    syncIdleCursor();
  };

  const zoomOut = () => {
    if (isMinZoom(zoomRef.current)) {
      return;
    }

    const focus = zoomFocusRef.current;

    applyViewport(
      {
        x: zoomAxisWindow(zoomRef.current.x, 'out', focus?.x),
        y: zoomAxisWindow(zoomRef.current.y, 'out', focus?.y),
      },
      true,
    );
    syncIdleCursor();
  };

  const resetZoom = () => {
    applyViewport({ x: { ...DEFAULT_ZOOM_WINDOW }, y: { ...DEFAULT_ZOOM_WINDOW } }, true);
    syncIdleCursor();
  };

  const zoomToMax = () => {
    if (isMaxZoom(zoomRef.current)) {
      return;
    }

    const focus = zoomFocusRef.current;

    applyViewport(
      {
        x: scaleZoomWindow(zoomRef.current.x, MIN_ZOOM_WINDOW, focus?.x),
        y: scaleZoomWindow(zoomRef.current.y, MIN_ZOOM_WINDOW, focus?.y),
      },
      true,
    );
    syncIdleCursor();
  };

  const zoomActionsRef = useRef({ zoomIn, zoomOut, resetZoom, zoomToMax });
  zoomActionsRef.current = { zoomIn, zoomOut, resetZoom, zoomToMax };

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !hasData) {
      return;
    }

    const chart = echarts.init(container);
    chartRef.current = chart;
    graphStore.registerChartInstance(chart);

    const setChartCursor = (cursor: string) => {
      chart.getZr().setCursorStyle(cursor);
      container.style.cursor = cursor;
    };

    const syncIdleCursor = () => {
      setChartCursor(isMinZoom(zoomRef.current) ? 'default' : 'grab');
    };

    syncIdleCursor();

    const clearHoverOverlays = () => {
      chart.dispatchAction({ type: 'hideTip' });
      chart.dispatchAction({ type: 'downplay' });
      activeHoverKeyRef.current = null;
      pointerXRef.current = null;
      pointerYRef.current = null;
    };

    const setOverlaysVisible = (visible: boolean) => {
      chart.setOption({
        tooltip: {
          show: visible,
          axisPointer: {
            show: visible,
          },
        },
      });

      if (!visible) {
        clearHoverOverlays();
      }
    };

    const updateHoverFromPointer = (offsetX: number, offsetY: number) => {
      if (isPanningRef.current) {
        return;
      }

      const currentMapped = mappedRef.current;

      if (!currentMapped.series.length || !chart.containPixel('grid', [offsetX, offsetY])) {
        clearHoverOverlays();
        return;
      }

      const pointerData = chart.convertFromPixel({ seriesIndex: 0 }, [offsetX, offsetY]);

      if (!pointerData) {
        clearHoverOverlays();
        return;
      }

      const [pointerX, pointerY] = pointerData;
      const ranges = resolveFullAxisRanges(currentMapped, axisLayout);
      const hovered = findNearestSeriesPoint(
        currentMapped.series,
        pointerX,
        pointerY,
        ranges.defaultX.max - ranges.defaultX.min,
        ranges.y.max - ranges.y.min,
      );

      if (!hovered) {
        clearHoverOverlays();
        return;
      }

      pointerXRef.current = hovered.point[0];
      pointerYRef.current = hovered.point[1];

      const hoverKey = `${hovered.seriesIndex}:${hovered.dataIndex}`;

      if (activeHoverKeyRef.current === hoverKey) {
        return;
      }

      const snapPixel = chart.convertToPixel({ seriesIndex: hovered.seriesIndex }, hovered.point);

      if (!snapPixel) {
        clearHoverOverlays();
        return;
      }

      activeHoverKeyRef.current = hoverKey;

      const tooltipPoints = collectTooltipSeriesPoints(currentMapped.series, hovered);

      chart.dispatchAction({ type: 'downplay' });
      tooltipPoints.forEach((item) => {
        chart.dispatchAction({
          type: 'highlight',
          seriesIndex: item.seriesIndex,
          dataIndex: item.dataIndex,
        });
      });

      chart.dispatchAction({
        type: 'updateAxisPointer',
        currTrigger: 'mousemove',
        x: snapPixel[0],
        y: snapPixel[1],
      });
      chart.dispatchAction({
        type: 'showTip',
        x: snapPixel[0],
        y: snapPixel[1],
      });
    };

    const stopPanning = () => {
      panRef.current = null;

      if (!isPanningRef.current) {
        syncIdleCursor();
        return;
      }

      isPanningRef.current = false;
      setZoom({
        x: { ...zoomRef.current.x },
        y: { ...zoomRef.current.y },
      });
      setOverlaysVisible(true);
      syncIdleCursor();
    };

    const handleGlobalOut = () => {
      clearHoverOverlays();
      stopPanning();
    };

    const resolvePanMetrics = (startZoom: ChartZoomState) => {
      const currentMapped = mappedRef.current;
      const ranges = resolveFullAxisRanges(currentMapped, axisLayout);
      const visibleX = getVisibleExtent(ranges.defaultX, startZoom.x);
      const visibleY = getVisibleExtent(ranges.y, startZoom.y);

      const topLeft = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [visibleX.min, visibleY.min]);
      const bottomRight = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [visibleX.max, visibleY.max]);

      if (!topLeft || !bottomRight) {
        return null;
      }

      const pixelWidth = Math.abs(bottomRight[0] - topLeft[0]);
      const pixelHeight = Math.abs(bottomRight[1] - topLeft[1]);

      if (!(pixelWidth > 0) || !(pixelHeight > 0)) {
        return null;
      }

      return {
        pixelToXPercent: ((startZoom.x.end - startZoom.x.start) / pixelWidth) * -1,
        pixelToYPercent: ((startZoom.y.end - startZoom.y.start) / pixelHeight) * (currentMapped.yInvert ? -1 : 1),
      };
    };

    const handleMouseDown = (event: { offsetX: number; offsetY: number }) => {
      if (isMinZoom(zoomRef.current)) {
        panRef.current = null;
        return;
      }

      const metrics = resolvePanMetrics(zoomRef.current);

      if (!metrics) {
        panRef.current = null;
        return;
      }

      panRef.current = {
        startX: event.offsetX,
        startY: event.offsetY,
        startZoom: {
          x: { ...zoomRef.current.x },
          y: { ...zoomRef.current.y },
        },
        pixelToXPercent: metrics.pixelToXPercent,
        pixelToYPercent: metrics.pixelToYPercent,
      };
      setChartCursor('grabbing');
    };

    const handleMouseMove = (event: {
      offsetX: number;
      offsetY: number;
      event?: { buttons?: number };
      buttons?: number;
    }) => {
      const buttons = event.event?.buttons ?? event.buttons ?? 0;
      const pan = panRef.current;

      if (buttons !== 1 || !pan) {
        const focus = resolveZoomFocusFromPixel(event.offsetX, event.offsetY);

        if (focus) {
          zoomFocusRef.current = focus;
        }

        updateHoverFromPointer(event.offsetX, event.offsetY);
        return;
      }

      const pixelDx = event.offsetX - pan.startX;
      const pixelDy = event.offsetY - pan.startY;

      if (!isPanningRef.current) {
        if (pixelDx * pixelDx + pixelDy * pixelDy < 9) {
          const focus = resolveZoomFocusFromPixel(event.offsetX, event.offsetY);

          if (focus) {
            zoomFocusRef.current = focus;
          }

          updateHoverFromPointer(event.offsetX, event.offsetY);
          return;
        }

        isPanningRef.current = true;
        setChartCursor('grabbing');
        setOverlaysVisible(false);
      }

      applyViewportRef.current(
        {
          x: shiftZoomWindow(pan.startZoom.x, pixelDx * pan.pixelToXPercent),
          y: shiftZoomWindow(pan.startZoom.y, pixelDy * pan.pixelToYPercent),
        },
        false,
      );
      setChartCursor('grabbing');
    };

    const handleMouseUp = () => {
      stopPanning();
    };

    chart.getZr().on('globalout', handleGlobalOut);
    chart.getZr().on('mousedown', handleMouseDown);
    chart.getZr().on('mousemove', handleMouseMove);
    chart.getZr().on('mouseup', handleMouseUp);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });

    resizeObserver.observe(container);

    return () => {
      chart.getZr().off('globalout', handleGlobalOut);
      chart.getZr().off('mousedown', handleMouseDown);
      chart.getZr().off('mousemove', handleMouseMove);
      chart.getZr().off('mouseup', handleMouseUp);
      resizeObserver.disconnect();
      graphStore.unregisterChartInstance(chart);
      chart.dispose();
      chartRef.current = null;
      activeHoverKeyRef.current = null;
      pointerXRef.current = null;
      pointerYRef.current = null;
      isPanningRef.current = false;
      panRef.current = null;
    };
    // TODO: need to resolve hidden dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chart instance lifecycle; zoom focus via render closure
  }, [graphStore, hasData, chartTypeId, config.id, axisLayout]);

  useEffect(() => {
    if (!hasData) {
      return;
    }

    const isEditableTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) {
        return false;
      }

      const tagName = target.tagName;

      return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || target.isContentEditable;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isHoveringChartRef.current || isPanningRef.current || isEditableTarget(event.target)) {
        return;
      }

      const { code, key } = event;
      const isZoomInKey = key === '+' || code === 'NumpadAdd' || code === 'Equal';
      const isZoomOutKey = key === '-' || code === 'NumpadSubtract' || code === 'Minus';
      const isMinZoomKey = code === 'Home';
      const isMaxZoomKey = code === 'End';

      if (!isZoomInKey && !isZoomOutKey && !isMinZoomKey && !isMaxZoomKey) {
        return;
      }

      event.preventDefault();

      if (isMinZoomKey) {
        zoomActionsRef.current.resetZoom();
        return;
      }

      if (isMaxZoomKey) {
        zoomActionsRef.current.zoomToMax();
        return;
      }

      if (isZoomInKey) {
        zoomActionsRef.current.zoomIn();
        return;
      }

      zoomActionsRef.current.zoomOut();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasData]);

  useEffect(() => {
    if (!hasData) {
      return;
    }

    intervalsRef.current = computeGridIntervals(mapped, zoomRef.current, axisLayout);
    activeHoverKeyRef.current = null;
    chartRef.current?.setOption(
      buildChartOption(
        mapped,
        xLabelsByField,
        yLabel,
        () => ({ x: pointerXRef.current, y: pointerYRef.current }),
        zoomRef.current,
        intervalsRef.current,
        () => isPanningRef.current,
        axisLayout,
      ),
      true,
    );
  }, [mapped, xLabelsByField, yLabel, hasData, axisLayout]);

  if (chartTypeId === 'ctcMd' && ctcStore.isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          <Loader size="m" />
        </div>
      </div>
    );
  }

  if (!hasData) {
    const emptyMessage = (() => {
      if (chartTypeId === 'ctcMd' && !ctcStore.hasOffsetTrajectories) {
        return t('Graph.noCtcReferences');
      }

      return t('Graph.noData');
    })();

    return (
      <div className={styles.root}>
        <TrajectoryChartWarnings
          warnings={mapped.excluded.map((item) => ({
            title: item.legendLabel,
            reason: item.reason,
          }))}
        />
        <div className={styles.emptyState}>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div
      className={styles.root}
      onMouseEnter={() => {
        isHoveringChartRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveringChartRef.current = false;
        zoomFocusRef.current = null;
      }}
    >
      <TrajectoryChartWarnings
        warnings={mapped.excluded.map((item) => ({
          title: item.legendLabel,
          reason: item.reason,
        }))}
      />
      <div className={styles.plotArea}>
        <div className={styles.zoomControlsZone}>
          <div className={styles.zoomControls}>
            <Button
              size="m"
              variant="flat"
              title={t('Graph.zoomIn')}
              className={styles.zoomButton}
              icon={<ZoomLightCustom />}
              disabled={!canZoomIn}
              onClick={zoomIn}
            />
            <Button
              size="m"
              variant="flat"
              title={t('Graph.zoomOut')}
              className={styles.zoomButton}
              icon={<ZoomOutCustom />}
              disabled={!canZoomOut}
              onClick={zoomOut}
            />
            <Button
              size="m"
              variant="flat"
              title={t('Graph.resetZoom')}
              className={styles.zoomButton}
              icon={<HistoryLightCustom />}
              disabled={!canZoomOut}
              onClick={resetZoom}
            />
          </div>
        </div>
        <div ref={containerRef} className={styles.chart} />
      </div>
      {legendItems.length > 0 ? (
        <div className={styles.legend}>
          {legendItems.map((item) => (
            <div key={item.key} className={styles.legendItem}>
              <span className={styles.legendSwatch} style={{ backgroundColor: item.color }} />
              <span className={styles.legendLabel} title={item.label}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
});
